import React, { useCallback } from 'react';
import { LoadingOverlay } from '../../core/screens/LoadingOverlay/LoadingOverlay';
import { useBankIdMachine, UseBankIdMachineProps } from '../hooks/useBankIdMachine';
import { AwaitingMobileBankIdAuthenticationTarget } from '../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingMobileBankIdAuthenticationContainer/molecules/AwaitingMobileBankIdAuthenticationTarget/AwaitingMobileBankIdAuthenticationTarget';
import { ApplicationEvent, PageView } from '../../models/analytics';
import { BackButton } from '../../components/organisms/Header/HeaderMolecule';
import { AwaitingMobileBankIdAuthenticationLocalPromptView } from '../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingMobileBankIdAuthenticationContainer/molecules/AwaitingMobileBankIdAuthenticationLocalPrompt/AwaitingMobileBankIdAuthenticationLocalPromptView';
import { BankIdAuthWithSupplementalContainer } from '../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingMobileBankIdAuthenticationContainer/organisms/BankIdAuthWithSupplementalContainer/BankIdAuthWithSupplementalContainer';
import { getBankIdAuthenticationType } from '../../utilities/provider-utils';
import { ProviderEntity } from '../../models/provider';
import { HandoffCredentialsAction } from '../../models/handoffSession';
import { CredentialsEntity } from '../../models/credentials';
import { useApplicationEvent } from '../../core/Analytics/useApplicationEvent';
import { BankIdRedirector } from '../../utilities/bankid-redirector/bankid-redirector';
import { shouldRedirectToReferPage } from '../../models/configuration';
import { useConfiguration } from '../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { useProduct } from '../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { FlowFinished } from '../../components/screens/FlowFinished/FlowFinished';
import { useCredentialsCopy } from '../../components/screens/SubmitCredentials/hooks/useCredentialsCopy';
import { HandoffCreatorMachineView } from './HandoffCreatorMachineView';

export type BankIdMachineViewProps = { provider: ProviderEntity } & UseBankIdMachineProps &
  BackButton;

export const BankIdMachineView = ({
  provider,
  onDone,
  bankIdTarget,
  credentials,
  onBack,
}: BankIdMachineViewProps) => {
  const { data } = useConfiguration();
  const [current, send] = useBankIdMachine({ onDone, bankIdTarget, credentials });
  const { sendEvent } = useApplicationEvent({
    applicationEvent: ApplicationEvent.BANK_ID_MACHINE,
  });

  const { productType } = useProduct();
  const { heading } = useCredentialsCopy();

  const onHandoffDone = useCallback(
    (credentialsUpdated: CredentialsEntity) => {
      send({ type: 'HANDOFF_DONE_EVENT', credentials: credentialsUpdated });
    },
    [send]
  );

  if (current.matches('APPLICATION_ERROR')) {
    sendEvent(ApplicationEvent.BANK_ID_MACHINE_ERROR);
    throw current.context.error;
  }

  if (current.matches('UI_AND_POLLER.UI.OPENING_BANK_ID_VIEW')) {
    return (
      <AwaitingMobileBankIdAuthenticationLocalPromptView
        isPay={false}
        onOpenBankId={() => {
          new BankIdRedirector({
            credentials: current.context.credentials,
            productType,
            redirectToReferPage: shouldRedirectToReferPage(data),
          }).open();
        }}
        onBack={onBack}
      />
    );
  }

  if (current.matches('HANDOFF_CREATOR_MACHINE')) {
    return (
      <HandoffCreatorMachineView
        providedCredentials={current.context.credentials}
        onHandoffDisable={() => {
          // does not get invoked for HandoffCredentialsAction.BANKID_AUTH
        }}
        onBack={onBack}
        onDone={onHandoffDone}
        provider={provider}
        handoffCredentialsAction={HandoffCredentialsAction.BANKID_AUTH}
      />
    );
  }

  if (current.matches('UI_AND_POLLER.UI.REMOTE_VIEW')) {
    return (
      <BankIdAuthWithSupplementalContainer
        onBack={onBack}
        heading={heading}
        provider={provider}
        supplementalInformation={{
          data: current.context.credentials.supplementalInformation!,
          type: getBankIdAuthenticationType(provider.name),
        }}
        isPay={false}
        footerDisclaimer={undefined}
      />
    );
  }

  if (current.matches('UI_AND_POLLER.UI.MOBILE_SELECT_TARGET_VIEW')) {
    return (
      <AwaitingMobileBankIdAuthenticationTarget
        pageView={PageView.BANKID_TARGET_AWAITING_SCREEN}
        provider={provider}
        onLocalDeviceClick={() => {
          send({ type: 'USER_CLICK_LOCAL_OPEN_BANK_ID_EVENT' });
        }}
        onRemoteDeviceClick={() => {
          send({ type: 'USER_CLICK_REMOTE_OPEN_BANK_ID_EVENT' });
        }}
        onBack={onBack}
        heading={heading}
        footerDisclaimer={undefined}
      />
    );
  }

  if (current.matches('FLOW_FINISHED_VIEW')) {
    return <FlowFinished />;
  }

  return <LoadingOverlay />;
};
